import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { 
  Calendar, 
  Phone, 
  Video, 
  Users, 
  CheckCircle2, 
  Clock, 
  XCircle,
  MapPin
} from 'lucide-react';
import { Badge } from '../../../ui-components/ui/badge';
import { Skeleton } from '../../../ui-components/ui/skeleton';
import { useAppointments } from '../../../lib/hooks/useAppointments';
import { Appointment, AppointmentStatus, AppointmentType } from '../../../lib/interfaces/appointment.interface';
import { cn } from '../../../lib/utils/utils';

interface ActivityTimelineProps {
  dealId: string;
  onActivityClick?: (activity: Appointment) => void;
  refreshTrigger?: number; // Add refresh trigger
}

const getActivityIcon = (type: AppointmentType) => {
  switch (type) {
    case AppointmentType.CALL:
      return Phone;
    case AppointmentType.VIDEO:
      return Video;
    case AppointmentType.SITE_VISIT:
      return MapPin;
    case AppointmentType.MEETING:
    default:
      return Users;
  }
};

const getStatusColor = (status: AppointmentStatus) => {
  switch (status) {
    case AppointmentStatus.SCHEDULED:
      return 'bg-blue-100 text-blue-800';
    case AppointmentStatus.DONE:
      return 'bg-green-100 text-green-800';
    case AppointmentStatus.CANCELED:
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getStatusIcon = (status: AppointmentStatus) => {
  switch (status) {
    case AppointmentStatus.SCHEDULED:
      return Clock;
    case AppointmentStatus.DONE:
      return CheckCircle2;
    case AppointmentStatus.CANCELED:
      return XCircle;
    default:
      return Clock;
  }
};

export const ActivityTimeline: React.FC<ActivityTimelineProps> = ({ dealId, onActivityClick, refreshTrigger }) => {
  const [activities, setActivities] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const { getAppointmentsByDeal } = useAppointments();

  useEffect(() => {
    const fetchActivities = async () => {
      setLoading(true);
      try {
        const appointments = await getAppointmentsByDeal(dealId);
        setActivities(appointments.sort((a, b) => 
          new Date(b.start).getTime() - new Date(a.start).getTime()
        ));
      } catch (error) {
        console.error('Error fetching activities:', error);
      } finally {
        setLoading(false);
      }
    };

    if (dealId) {
      fetchActivities();
    }
  }, [dealId, getAppointmentsByDeal, refreshTrigger]); // Add refreshTrigger to dependencies

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-3">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Calendar className="h-12 w-12 mx-auto mb-2 text-gray-300" />
        <p>Keine Aktivitäten vorhanden</p>
      </div>
    );
  }

  const upcomingActivities = activities.filter(
    a => a.status === AppointmentStatus.SCHEDULED && new Date(a.start) > new Date()
  );
  const pastActivities = activities.filter(
    a => a.status !== AppointmentStatus.SCHEDULED || new Date(a.start) <= new Date()
  );

  return (
    <div className="space-y-6">
      {upcomingActivities.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Anstehende Aktivitäten</h4>
          <div className="space-y-3">
            {upcomingActivities.map((activity) => (
              <ActivityItem 
                key={activity._id} 
                activity={activity} 
                onClick={() => onActivityClick?.(activity)}
                isUpcoming
              />
            ))}
          </div>
        </div>
      )}

      {pastActivities.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Vergangene Aktivitäten</h4>
          <div className="space-y-3">
            {pastActivities.map((activity) => (
              <ActivityItem 
                key={activity._id} 
                activity={activity} 
                onClick={() => onActivityClick?.(activity)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

interface ActivityItemProps {
  activity: Appointment;
  onClick?: () => void;
  isUpcoming?: boolean;
}

const ActivityItem: React.FC<ActivityItemProps> = ({ activity, onClick, isUpcoming }) => {
  const Icon = getActivityIcon(activity.type);
  const StatusIcon = getStatusIcon(activity.status);
  const statusColor = getStatusColor(activity.status);

  return (
    <div 
      className={cn(
        "flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
        "hover:bg-gray-50",
        isUpcoming && "border-blue-200 bg-blue-50/50"
      )}
      onClick={onClick}
    >
      <div className={cn(
        "flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center",
        isUpcoming ? "bg-blue-100" : "bg-gray-100"
      )}>
        <Icon className={cn("h-5 w-5", isUpcoming ? "text-blue-600" : "text-gray-600")} />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <h5 className="font-medium text-sm text-gray-900 truncate">
              {activity.title}
            </h5>
            <p className="text-xs text-gray-500 mt-1">
              {format(new Date(activity.start), 'dd. MMM yyyy • HH:mm', { locale: de })}
              {activity.location && (
                <span className="ml-2">
                  <MapPin className="inline h-3 w-3 mr-1" />
                  {activity.location}
                </span>
              )}
            </p>
            {activity.description && (
              <p className="text-xs text-gray-600 mt-2 line-clamp-2">
                {activity.description}
              </p>
            )}
          </div>
          
          <Badge variant="secondary" className={cn("text-xs", statusColor)}>
            <StatusIcon className="h-3 w-3 mr-1" />
            {activity.status === AppointmentStatus.SCHEDULED ? 'Geplant' : 
             activity.status === AppointmentStatus.DONE ? 'Erledigt' : 'Abgesagt'}
          </Badge>
        </div>
      </div>
    </div>
  );
};

export default ActivityTimeline;
